import { Link } from '@ecomm/framework/Link'
import { NativeVideo } from '@ecomm/shared-components/lib/NativeVideo'
import { useTrackingLiveGuardCta } from '@ecomm/tracking/useTrackingLiveGuardCta'
import { useCallback } from 'react'
import type { TypeOf } from 'zod'

import { trackMonitoringCTA } from '@ecomm/cdp-tracking'
import type { schema } from './schema'

export type VideoBannerProps = TypeOf<typeof schema>

type Props = {
  readonly data: VideoBannerProps
}

function VideoBanner({ data }: Props) {
  const {
    badge,
    preTitle,
    title,
    postTitle,
    text,
    buttonText,
    redirectUrl,
    video
  } = data

  const trackMonitoringCtaClick = useTrackingLiveGuardCta()

  const handleClick = useCallback(() => {
    trackMonitoringCtaClick()
    trackMonitoringCTA()
  }, [trackMonitoringCtaClick])

  return (
    <div
      className="bg-neutral-black flex flex-col gap-5 rounded-2xl lg:flex-row px-4 py-8 md:p-8 lg:grid-cols-2 lg:px-12 lg:py-8"
      data-component="VideoBanner"
      data-testid="VideoBanner"
    >
      <div className="m-auto w-full">
        <NativeVideo
          captionsSrcUrl={video.captionsSrc}
          muted={false}
          poster={video.posterImage}
          videoSrcUrlMp4={video.mp4Link}
          videoSrcUrlWebm={video.webmLink}
          ariaLabel={video.ariaLabel ?? undefined}
        />
      </div>
      <div className="flex flex-col gap-4 md:mx-0 lg:order-first lg:max-w-[30rem]">
        <div className="bg-primary-100 w-fit rounded-full py-2 px-3 text-sm font-bold leading-none">
          {badge}
        </div>
        <div className="flex flex-col md:gap-3">
          {preTitle ? (
            <h4
              className="m-0 text-sm leading-8 md:text-2xl font-semibold text-white font-arizona lg:min-w-[500px]"
              aria-label={preTitle}
            >
              {preTitle.split(/(®)/).map((part, index) =>
                part === '®' ? (
                  <sup className="md:text-sm" key={index}>
                    ®
                  </sup>
                ) : (
                  part
                )
              )}
            </h4>
          ) : null}
          <h2 className="text-[28px] leading-8 md:text-[55px] md:leading-[60px] mb-0 md:mb-2 text-white">
            {title}
          </h2>
          {postTitle ? (
            <h4
              className="m-0 text-sm leading-8 md:text-xl font-semibold text-white font-arizona lg:min-w-[500px]"
              aria-label={postTitle}
            >
              {postTitle.split(/(®)/).map((part, index) =>
                part === '®' ? (
                  <sup className="md:text-sm" key={index}>
                    ®
                  </sup>
                ) : (
                  part
                )
              )}
            </h4>
          ) : null}
        </div>
        <p className="text-white">{text}</p>
        <Link
          className="hover:text-btn-secondary w-full rounded-md border-2 border-solid border-white px-4 py-3 text-center text-white no-underline duration-200 ease-in-out lg:w-fit"
          onClick={handleClick}
          to={redirectUrl}
        >
          {buttonText}
        </Link>
      </div>
    </div>
  )
}

export { VideoBanner }
